import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Truck, Users, LogOut, Menu, X, CalendarDays, Globe, Gauge, Building2, Wrench, Briefcase, FileText, Mail, ShieldCheck, Eye, MapPin, Info, MessageSquareHeart, Ship, CreditCard, FlaskConical, RotateCcw, ClipboardCheck, ReceiptText } from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AuditReportDialog } from "@/components/AuditReportDialog";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/operator", label: "Operator", icon: Building2, id: "operator" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
  { to: "/drivers", label: "Drivers", icon: Users, id: "drivers" },
  { to: "/vehicles", label: "Fleet", icon: Truck, id: "vehicles" },
  { to: "/review-inbox", label: "Review", icon: ClipboardCheck, id: "review-inbox", when: (u) => u?.role === "manager" || u?.is_admin },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, id: "maintenance" },
  { to: "/office", label: "Office", icon: Briefcase, id: "office" },
  { to: "/invoices", label: "Invoices", icon: ReceiptText, id: "invoices", when: (u) => u?.role === "manager" || u?.is_admin },
  { to: "/tacho", label: "Tacho Portal", icon: Gauge, id: "tacho" },
  { to: "/tracking", label: "Tracking", icon: MapPin, id: "tracking", when: (u) => u?.role === "manager" || u?.is_admin },
  { to: "/movements", label: "Movements", icon: Ship, id: "movements" },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "billing", when: (u) => u?.role === "manager" },
  { to: "/test-mode", label: "Test Mode", icon: FlaskConical, id: "test-mode", when: (u) => u?.role === "manager" && !u?.is_sandbox && !u?.impersonating },
  { to: "/admin", label: "Admin", icon: ShieldCheck, id: "admin", when: (u) => u?.is_admin, variant: "admin" },
  { to: "/contact", label: "Contact", icon: Mail, id: "contact" },
  { to: "/feedback", label: "Feedback", icon: MessageSquareHeart, id: "feedback" },
  { to: "/about", label: "About", icon: Info, id: "about" },
];

export default function Layout({ children }) {
  const { user, logout, exitViewAs, enterSandbox, exitSandbox, resetSandbox, exitDemo } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [auditOpen, setAuditOpen] = useState(false);
  const [sharedUnseen, setSharedUnseen] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [billing, setBilling] = useState(null);

  const handleExitViewAs = async () => {
    await exitViewAs();
    navigate("/admin");
  };

  const roleMatches = (r) => user?.preview_role === (r === "owner" ? "manager" : r);
  const switchSandboxRole = async (r) => {
    try { await enterSandbox(r); navigate("/dashboard"); }
    catch { toast.error("Could not switch test role"); }
  };
  const handleExitSandbox = async () => {
    await exitSandbox();
    navigate("/test-mode");
  };
  const handleResetSandbox = async () => {
    if (!window.confirm("Reset the test company back to its sample data? This only affects Test Mode.")) return;
    try { await resetSandbox(); toast.success("Test data reset"); window.location.reload(); }
    catch { toast.error("Could not reset test data"); }
  };

  const AuditButton = () => (
    <div className="px-3 pb-3">
      <button
        data-testid="fleet-audit-button"
        onClick={() => { setAuditOpen(true); setOpen(false); }}
        className="w-full flex items-center justify-center gap-2 bg-white text-slate-900 hover:bg-slate-100 font-semibold text-sm rounded-md py-2.5 transition-colors"
      >
        <FileText size={16} /> Fleet Audit Report
      </button>
    </div>
  );

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/alerts/unread-count");
        if (active) setUnread(data.count || 0);
      } catch { /* ignore */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => { active = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    document.title = unread > 0 ? `(${unread}) HaulCheck — Defect alerts` : "HaulCheck";
  }, [unread]);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/global-docs/unseen-count");
        if (active) setSharedUnseen(data.count || 0);
      } catch { /* ignore */ }
    };
    poll();
    const onSeen = () => setSharedUnseen(0);
    window.addEventListener("shared-docs-seen", onSeen);
    const t = setInterval(poll, 60000);
    return () => { active = false; clearInterval(t); window.removeEventListener("shared-docs-seen", onSeen); };
  }, []);

  useEffect(() => {
    if (!user || user.role !== "manager") { setBilling(null); return; }
    let active = true;
    const load = async () => { try { const { data } = await api.get("/billing"); if (active) setBilling(data); } catch { /* ignore */ } };
    load();
    const t = setInterval(load, 300000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  useEffect(() => {
    if (!user || !(user.role === "manager" || user.is_admin)) { setReviewCount(0); return; }
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/review-inbox");
        if (active) setReviewCount(data.count || 0);
      } catch { /* ignore */ }
    };
    poll();
    const onUpd = () => poll();
    window.addEventListener("review-inbox-updated", onUpd);
    const t = setInterval(poll, 60000);
    return () => { active = false; clearInterval(t); window.removeEventListener("review-inbox-updated", onUpd); };
  }, [user]);

  const NavItems = () => (
    <nav className="flex flex-col gap-1">
      {NAV.filter((item) => !item.when || item.when(user)).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          data-testid={`nav-${item.id}`}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-all duration-200 border-l-2",
              item.variant === "admin"
                ? (isActive
                    ? "bg-amber-500/20 text-amber-300 border-amber-400"
                    : "text-amber-300/70 border-transparent hover:text-amber-200 hover:bg-slate-800/60")
                : (isActive
                    ? "bg-slate-800 text-white border-white"
                    : "text-slate-400 border-transparent hover:text-white hover:bg-slate-800/60")
            )
          }
        >
          <item.icon size={20} />
          {item.label}
          {item.id === "dashboard" && unread > 0 && (
            <span data-testid="nav-alert-badge" className="ml-auto bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{unread}</span>
          )}
          {item.id === "office" && sharedUnseen > 0 && (
            <span data-testid="nav-shared-badge" className="ml-auto bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{sharedUnseen}</span>
          )}
          {item.id === "review-inbox" && reviewCount > 0 && (
            <span data-testid="nav-review-badge" className="ml-auto bg-amber-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{reviewCount}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-52 flex-col bg-slate-900 text-white sticky top-0 h-screen">
        <div className="px-6 py-6 flex items-center gap-2 border-b border-slate-800">
          <Truck size={26} className="text-white" />
          <div>
            <p className="font-heading font-black text-lg leading-none tracking-tight">HAULCHECK</p>
            <p className="text-[10px] tracking-[0.25em] text-slate-400 uppercase mt-1">Compliance</p>
          </div>
        </div>
        <div className="flex-1 py-4 overflow-y-auto"><NavItems /></div>
        <AuditButton />
      </aside>

      {/* Mobile header */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between bg-slate-900 text-white px-4 py-3 sticky top-0 z-30">
          <div className="flex items-center gap-2">
            <Truck size={22} />
            <span className="font-heading font-black tracking-tight">HAULCHECK</span>
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </header>
        {open && (
          <div className="md:hidden bg-slate-900 text-white pb-4">
            <NavItems />
            <div className="mt-2"><AuditButton /></div>
            <div className="px-4 pt-3 text-xs text-slate-400 truncate" data-testid="mobile-user-email">{user?.email}</div>
            <button
              data-testid="mobile-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-sm text-slate-400 px-4 py-3"
            >
              <LogOut size={16} /> Sign out
            </button>
          </div>
        )}

        <main className="flex-1 p-6 sm:p-8 md:p-10 max-w-[1680px] w-full mx-auto">
          <div className="hidden md:flex items-center justify-end gap-3 mb-6" data-testid="top-user-bar">
            {billing && (billing.status === "trialing" || billing.status === "expired") && (
              <button
                data-testid="top-trial-chip"
                onClick={() => navigate("/billing")}
                className={`text-xs font-semibold rounded-full px-3 py-1.5 transition-opacity hover:opacity-90 ${billing.status === "expired" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}
              >
                {billing.status === "expired" ? "Trial ended — subscribe" : `Trial · ${billing.trial_days_remaining} days left`}
              </button>
            )}
            <span data-testid="top-user-email" className="text-sm text-slate-600 font-medium truncate max-w-[240px]">{user?.email}</span>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900 border border-slate-300 rounded-md px-3 py-1.5 bg-white hover:bg-slate-50 transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
          {billing && (billing.status === "expired" || billing.status === "past_due") && (
            <div data-testid="billing-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
              <span className="flex items-center gap-2">
                <CreditCard size={15} className="shrink-0" />
                <span><b>{billing.status === "expired" ? "Your free trial has ended." : "A payment is due."}</b> Subscribe to keep full access to HaulCheck.</span>
              </span>
              <button
                data-testid="billing-banner-cta"
                onClick={() => navigate("/billing")}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 transition-colors"
              >
                Go to Billing
              </button>
            </div>
          )}
          {user?.is_demo && (
            <div data-testid="demo-banner" className="mb-6 rounded-md border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <Eye size={16} className="shrink-0 text-emerald-600" />
                  <span><b>Live demo — sample data.</b> This is a read-only example fleet, not a real company. Explore the dashboard, scores and alerts.</span>
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to="/trial" data-testid="demo-start-trial" className="text-xs font-bold rounded-md bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700">Start free trial</Link>
                  <button data-testid="exit-demo-button" onClick={exitDemo}
                    className="text-xs font-semibold rounded-md bg-white border border-emerald-300 text-emerald-700 px-2.5 py-1.5 hover:bg-emerald-100 inline-flex items-center gap-1">
                    <X size={12} /> Exit demo
                  </button>
                </div>
              </div>
            </div>
          )}
          {user?.is_sandbox && !user?.is_demo && (
            <div data-testid="sandbox-banner" className="mb-6 rounded-md border-2 border-violet-300 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                <span className="flex items-center gap-2">
                  <FlaskConical size={16} className="shrink-0 text-violet-600" />
                  <span><b>TEST MODE — isolated sandbox company.</b> Your live data is completely safe; nothing here touches your real account.</span>
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-violet-700">Viewing as:</span>
                  {[["owner", "Owner"], ["staff", "Staff"], ["viewer", "Viewer"]].map(([r, l]) => (
                    <button key={r} data-testid={`sandbox-role-${r}`} onClick={() => switchSandboxRole(r)}
                      className={cn("text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors",
                        roleMatches(r) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-700 border-violet-300 hover:bg-violet-100")}>
                      {l}
                    </button>
                  ))}
                  <button data-testid="sandbox-reset-button" onClick={handleResetSandbox}
                    className="text-xs font-semibold rounded-md bg-white border border-violet-300 text-violet-700 px-2.5 py-1 hover:bg-violet-100 inline-flex items-center gap-1">
                    <RotateCcw size={12} /> Reset data
                  </button>
                  <button data-testid="exit-sandbox-button" onClick={handleExitSandbox}
                    className="text-xs font-semibold rounded-md bg-violet-600 text-white px-2.5 py-1 hover:bg-violet-700 inline-flex items-center gap-1">
                    <X size={12} /> Exit test mode
                  </button>
                </div>
              </div>
            </div>
          )}
          {user?.impersonating && (
            <div data-testid="impersonation-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
              <span className="flex items-center gap-2">
                <Eye size={15} className="shrink-0" />
                <span><span className="font-semibold">Viewing as {user?.name || user?.email}.</span> Read-only — changes are disabled. You're signed in as admin {user?.impersonated_by_email}.</span>
              </span>
              <button
                data-testid="exit-view-as-button"
                onClick={handleExitViewAs}
                className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-800 transition-colors"
              >
                <X size={13} /> Exit view
              </button>
            </div>
          )}
          {user?.role === "viewer" && !user?.is_demo && (
            <div data-testid="viewer-banner" className="mb-6 flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50 px-4 py-2.5 text-sm text-sky-800">
              <Globe size={15} className="shrink-0" />
              <span><span className="font-semibold">Read-only access.</span> You can view everything but changes are disabled.</span>
            </div>
          )}
          {children}
          <footer className="mt-12 pt-5 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400" data-testid="app-footer">
            <span>© {new Date().getFullYear()} HaulCheck</span>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
              <Link to="/dpa" className="hover:text-slate-700">DPA</Link>
              <Link to="/contact" className="hover:text-slate-700">Contact</Link>
            </div>
          </footer>
        </main>
      </div>
      <AuditReportDialog open={auditOpen} onOpenChange={setAuditOpen} />
    </div>
  );
}
